import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { VStack } from '@astryxdesign/core/VStack';
import { HStack } from '@astryxdesign/core/HStack';
import { Heading } from '@astryxdesign/core/Heading';
import { Text } from '@astryxdesign/core/Text';
import { Card } from '@astryxdesign/core/Card';
import { Badge } from '@astryxdesign/core/Badge';
import { Button } from '@astryxdesign/core/Button';
import { AlertDialog } from '@astryxdesign/core/AlertDialog';
import { MetadataList, MetadataListItem } from '@astryxdesign/core/MetadataList';
import { useToast } from '@astryxdesign/core/Toast';
import { useTenant } from '../../app/tenant-context';
import { publicApi, ApiError, type ClientBooking } from '../../lib/public-api';
import { forgetBooking, listSaved, type SavedBooking } from '../../lib/my-bookings';
import { Empty, ErrorView, LoadingRows } from '../../components/StateViews';
import { ReminderOffer } from './ReminderOffer';
import { formatDateTime, formatMoney } from '../../../supabase/functions/_shared/domain/format.ts';

const STATUS: Record<ClientBooking['status'], { label: string; variant: 'info' | 'success' | 'neutral' | 'error' | 'warning' }> = {
  confirmed: { label: 'Подтверждена', variant: 'info' },
  in_progress: { label: 'В работе', variant: 'warning' },
  completed: { label: 'Готово', variant: 'success' },
  cancelled: { label: 'Отменена', variant: 'neutral' },
  no_show: { label: 'Не состоялась', variant: 'error' },
};

export function MyBookingPage() {
  const { slug, sf, openBooking } = useTenant();
  const saved = listSaved(slug);
  const q = useQuery({
    queryKey: ['my-bookings', slug, saved.map((s) => s.id).join(',')],
    queryFn: async () => {
      const out: Array<{ saved: SavedBooking; booking: ClientBooking | null }> = [];
      for (const s of saved) {
        try {
          out.push({ saved: s, booking: await publicApi.booking(slug, s.token) });
        } catch (e) {
          if (e instanceof ApiError && e.status === 404) { forgetBooking(slug, s.id); continue; }
          throw e;
        }
      }
      return out;
    },
    // private per-device data: always from the network, never persisted
    gcTime: 60_000,
  });

  const now = Date.now();
  const items = (q.data ?? []).filter((x) => x.booking);
  const upcoming = items.filter((x) => x.booking!.status !== 'cancelled' && Date.parse(x.booking!.ends_at) > now);
  const past = items.filter((x) => !upcoming.includes(x));

  return (
    <VStack gap={5} padding={4} className="page safe-top">
      <Heading level={1}>Моя запись</Heading>
      {q.isPending ? <LoadingRows rows={2} height={140} label="Загружаем ваши записи" /> :
       q.isError ? <ErrorView error={q.error} onRetry={() => q.refetch()} /> :
       items.length === 0 ? (
        <Empty title="Записей на этом устройстве нет" description={`Запишитесь онлайн — запись появится здесь. Если записывались с другого телефона, позвоните: ${sf.profile.phone}.`}
          action={<Button label="Записаться" variant="primary" onClick={() => openBooking()} />} />
       ) : (
        <VStack gap={4}>
          {upcoming.map((x) => <BookingCard key={x.saved.id} saved={x.saved} booking={x.booking!} />)}
          {past.length > 0 ? <Heading level={2}>Прошедшие и отменённые</Heading> : null}
          {past.map((x) => <BookingCard key={x.saved.id} saved={x.saved} booking={x.booking!} compact />)}
        </VStack>
       )}
    </VStack>
  );
}

function BookingCard({ saved, booking, compact }: { saved: SavedBooking; booking: ClientBooking; compact?: boolean }) {
  const { slug, sf } = useTenant();
  const qc = useQueryClient();
  const showToast = useToast();
  const [confirm, setConfirm] = useState(false);
  const tz = sf.tenant.timezone;
  const cancel = useMutation({
    mutationFn: () => publicApi.cancel(slug, saved.token, 'Отменено клиентом'),
    onSuccess: () => {
      setConfirm(false);
      showToast({ body: 'Запись отменена' });
      void qc.invalidateQueries({ queryKey: ['my-bookings', slug] });
      void qc.invalidateQueries({ queryKey: ['slots', slug] });
    },
    onError: (e) => showToast({ body: (e as Error).message, type: 'error' }),
  });
  const st = STATUS[booking.status];
  return (
    <Card padding={4}>
      <VStack gap={3}>
        <HStack hAlign="between" vAlign="center" gap={2}>
          <Text weight="semibold" type="large">{booking.service_name}</Text>
          <Badge label={st.label} variant={st.variant} />
        </HStack>
        <Text>{formatDateTime(booking.starts_at, tz)}</Text>
        {!compact ? (
          <>
            <MetadataList columns="single">
              <MetadataListItem label="Код">{booking.public_code}</MetadataListItem>
              <MetadataListItem label="Автомобиль">{[booking.car, booking.car_plate].filter(Boolean).join(', ') || '—'}</MetadataListItem>
              <MetadataListItem label="Стоимость">{formatMoney(booking.price_cents, booking.currency, booking.price_is_from)}</MetadataListItem>
              <MetadataListItem label="Адрес">{sf.profile.address}</MetadataListItem>
            </MetadataList>
            {booking.status === 'confirmed' ? <ReminderOffer booking={booking} token={saved.token} /> : null}
            {booking.can_cancel ? (
              <Button label="Отменить запись" variant="destructive" onClick={() => setConfirm(true)} />
            ) : booking.status === 'confirmed' ? (
              <Text type="supporting">Отменить онлайн уже нельзя. Позвоните: {sf.profile.phone}</Text>
            ) : null}
          </>
        ) : null}
      </VStack>
      <AlertDialog isOpen={confirm} onOpenChange={setConfirm} title="Отменить запись?" description={`${booking.service_name}, ${formatDateTime(booking.starts_at, tz)}. Время станет доступно другим клиентам.`}
        actionLabel="Отменить запись" cancelLabel="Оставить" actionVariant="destructive" isActionLoading={cancel.isPending} onAction={() => cancel.mutate()} />
    </Card>
  );
}
